import { Box, Card, Flex, Text } from "@chakra-ui/react";
import { TrendingDown, TrendingUp } from "lucide-react";
import useNumberFormatter from "../hooks/useNumberFormatter";
import { ElementType } from "react";

export type ProgressProps = {
  value: number; // percentage (0-100)
  label: string;
  color: string;
};

type Props = {
  title: string;
  stat: string | number | undefined;
  icon: ElementType;
  progress?: ProgressProps;
  percentages: {
    value: number | undefined;
    type: "increase" | "decrease" | undefined;
  };
  format?: boolean;
};

const StatCard = ({
  title,
  stat,
  icon: IconComponent,
  progress,
  percentages,
  format = false,
}: Props): JSX.Element => {
  const formatNumber = useNumberFormatter("es-CO", "COP", "number");

  const trendColor = percentages.type === "increase" ? "#3CB18B" : "#EC5858";
  const trendBg = percentages.type === "increase" ? "#CFF5D1" : "#FFCBCB";

  return (
    <Card.Root
      bg="white"
      rounded="lg"
      border="none"
      boxShadow="rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
      p={4}
    >
      <Card.Body p={0} display="flex" flexDirection="column">
        {/* Top Row: Title & Icon */}
        <Flex justifyContent="space-between" alignItems="center" mb={1}>
          <Text fontSize="14px" fontWeight="600" color="#54596C">
            {title}
          </Text>
          <Flex
            alignItems="center"
            justifyContent="center"
            w="32px"
            h="32px"
            bg="#F3F5FC"
            borderRadius="md"
          >
            <IconComponent size={16} color="#A7ACB8" />
          </Flex>
        </Flex>

        {/* Middle: Big Stat */}
        <Text fontSize="24px" fontWeight="700" color="#1A202C" mb={4}>
          {format ? formatNumber(Number(stat)) : stat}
        </Text>

        {/* Progress Bar (Optional) */}
        {progress && (
          <Box mb={4}>
            <Text fontSize="12px" color="#858D9D" mb={1} fontWeight="500">
              {progress.label}
            </Text>
            <Box
              w="100%"
              h="4px"
              bg="#F1F3F5"
              borderRadius="full"
              overflow="hidden"
            >
              <Box
                h="100%"
                bg={progress.color}
                w={`${Math.min(Math.max(progress.value, 0), 100)}%`}
                borderRadius="full"
              />
            </Box>
          </Box>
        )}

        {/* Bottom Row: Trend & Text */}
        <Flex alignItems="center" gap={2} mt="auto">
          <Flex
            alignItems="center"
            gap={1}
            px={2}
            py={0.5}
            borderRadius="full"
            bg={trendBg}
            color={trendColor}
          >
            {percentages.type === "increase" ? (
              <TrendingUp size={12} strokeWidth={2.5} />
            ) : (
              <TrendingDown size={12} strokeWidth={2.5} />
            )}
            <Text fontSize="12px" fontWeight="700">
              {percentages.value}%
            </Text>
          </Flex>
          <Text fontSize="12px" color="#858D9D" fontWeight="500">
            vs mes anterior
          </Text>
        </Flex>
      </Card.Body>
    </Card.Root>
  );
};

export default StatCard;
